import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ATMController.module.css';
import {
    atmActionHistoryService,
    AtmActionHistoryEntry,
    AtmActionHistorySummary,
    AtmActionType,
} from '../../services/atmActionHistory.service';

/** Small enough that the first screenful arrives quickly, since this loads as you scroll. */
const PAGE_SIZE = 20;
const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_RANGE_DAYS = 7;

// An explicit "From" pins the query to the beginning of time so a cleared
// filter shows the whole log rather than falling into the backend's own
// 7-day default (which only kicks in when both `from` and `to` are omitted).
const ALL_TIME_FROM = new Date(0).toISOString();

// `<input type="date">` gives a bare day; the backend filters on a DateTime,
// so widen each end to cover the whole local day rather than midnight UTC.
const startOfDay = (day: string): string => new Date(`${day}T00:00:00`).toISOString();
const endOfDay = (day: string): string => new Date(`${day}T23:59:59.999`).toISOString();

// `<input type="date">` wants a local YYYY-MM-DD, not an ISO instant.
const asDayInput = (d: Date): string =>
    new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const ACTION_LABELS: Record<AtmActionType, string> = {
    cash_in: 'Cash In',
    cash_out: 'Cash Out',
    registration: 'Registration',
};

const actionClass = (action: AtmActionType): string => {
    switch (action) {
        case 'cash_in':
            return styles.historyCashIn;
        case 'cash_out':
            return styles.historyCashOut;
        default:
            return styles.historyRegistration;
    }
};

const formatWhen = (dateStr: string) =>
    new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

// Registration rows carry no amount, so a dash is the honest reading — $0.00
// would look like a zero-value transaction.
const formatAmount = (amount: number | null) =>
    amount == null ? '—' : `$${Number(amount).toFixed(2)}`;

const playerOf = (entry: AtmActionHistoryEntry): string =>
    entry.user?.username ?? entry.atmExternalUser?.username ?? '—';

// Summary totals are always present, unlike a row's nullable amount, so these
// never render the em-dash that formatAmount uses.
const formatTotal = (v: number) => `$${Math.abs(v).toFixed(2)}`;
const formatProfit = (v: number) => `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;

/**
 * This terminal's action log, newest first, loaded a page at a time as the list
 * is scrolled. A terminal accumulates thousands of rows, so the alternative —
 * one query for all of them behind an already-heavy detail panel — is not one.
 */
const AtmTerminalHistory = ({ atmId }: { atmId: number }) => {
    const [items, setItems] = useState<AtmActionHistoryEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Independent of pagination — every page carries the same totals for the
    // selected range, so it only needs setting once, from the first page.
    const [summary, setSummary] = useState<AtmActionHistorySummary | null>(null);
    // Defaults to the last week rather than the empty (all-time) state, so the
    // panel opens on a bounded, quick-to-scan window.
    const [filterFrom, setFilterFrom] = useState(() =>
        asDayInput(new Date(Date.now() - DEFAULT_RANGE_DAYS * DAY_MS)),
    );
    const [filterTo, setFilterTo] = useState(() => asDayInput(new Date()));

    const listRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    // The observer can fire again before React has re-rendered with the new
    // `loading`, which would fetch the same offset twice and duplicate rows.
    const busyRef = useRef(false);
    const offsetRef = useRef(0);
    // Offsets already asked for. StrictMode runs the mount effect twice in
    // development, and both passes reset `busyRef` before firing, so that guard
    // alone lets page 0 be fetched twice — which showed up as 107 rows for a
    // terminal with 87.
    const requestedRef = useRef<Set<number>>(new Set());

    const loadMore = useCallback(async () => {
        if (busyRef.current) return;
        const offset = offsetRef.current;
        if (requestedRef.current.has(offset)) return;
        requestedRef.current.add(offset);
        busyRef.current = true;
        setLoading(true);
        setError(null);
        try {
            const data = await atmActionHistoryService.getHistory({
                atmId,
                from: filterFrom ? startOfDay(filterFrom) : ALL_TIME_FROM,
                ...(filterTo ? { to: endOfDay(filterTo) } : {}),
                limit: PAGE_SIZE,
                offset,
            });
            // Append only what we don't already hold: a page can arrive twice
            // (see `requestedRef`), and duplicate ids would also collide as
            // React keys.
            setItems((prev) => {
                const seen = new Set(prev.map((item) => item.id));
                return [...prev, ...data.items.filter((item) => !seen.has(item.id))];
            });
            setTotal(data.pageInfo.total);
            setHasMore(data.pageInfo.hasNextPage);
            offsetRef.current = data.pageInfo.nextOffset ?? offset + data.items.length;
            if (offset === 0) setSummary(data.summary);
        } catch (err: any) {
            setError(err?.message || 'Failed to load history');
            setHasMore(false); // don't let the observer retry a failing page forever
            if (offset === 0) setSummary(null);
        } finally {
            busyRef.current = false;
            setLoading(false);
        }
    }, [atmId, filterFrom, filterTo]);

    // A different terminal, or a different date range, is a different list —
    // start over.
    useEffect(() => {
        offsetRef.current = 0;
        busyRef.current = false;
        requestedRef.current = new Set();
        setItems([]);
        setTotal(0);
        setHasMore(true);
        setError(null);
        setSummary(null);
        loadMore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [atmId, filterFrom, filterTo]);

    // Rooted at the list, not the viewport: the panel it lives in scrolls too,
    // and a viewport-rooted observer would fire on the wrong scroll.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        const root = listRef.current;
        if (!sentinel || !root || !hasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) loadMore();
            },
            // Start fetching a little before the end so the next rows are
            // usually there by the time the player reaches them.
            { root, rootMargin: '120px' },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadMore, items.length]);

    return (
        <>
            <div className={styles.historyFilters}>
                <label className={styles.historyFilterField}>
                    <span className={styles.historyFilterLabel}>From</span>
                    <input
                        className={styles.historyFilterInput}
                        type="date"
                        value={filterFrom}
                        max={filterTo || undefined}
                        onChange={(e) => setFilterFrom(e.target.value)}
                    />
                </label>
                <label className={styles.historyFilterField}>
                    <span className={styles.historyFilterLabel}>To</span>
                    <input
                        className={styles.historyFilterInput}
                        type="date"
                        value={filterTo}
                        min={filterFrom || undefined}
                        onChange={(e) => setFilterTo(e.target.value)}
                    />
                </label>
                {(filterFrom || filterTo) && (
                    <button
                        type="button"
                        className={styles.historyFilterClearBtn}
                        onClick={() => {
                            setFilterFrom('');
                            setFilterTo('');
                        }}
                    >
                        All time
                    </button>
                )}
            </div>

            {summary && (
                <div className={styles.historySummary}>
                    <div className={styles.historySummaryItem}>
                        <span className={styles.historySummaryLabel}>Cash In</span>
                        <span className={`${styles.historySummaryValue} ${styles.historySummaryCashIn}`}>
                            {formatTotal(summary.totalCashIn)}
                        </span>
                    </div>
                    <div className={styles.historySummaryItem}>
                        <span className={styles.historySummaryLabel}>Cash Out</span>
                        <span className={`${styles.historySummaryValue} ${styles.historySummaryCashOut}`}>
                            {formatTotal(summary.totalCashOut)}
                        </span>
                    </div>
                    <div className={styles.historySummaryItem}>
                        <span className={styles.historySummaryLabel}>Profit</span>
                        <span
                            className={`${styles.historySummaryValue} ${
                                summary.profit < 0 ? styles.historySummaryCashOut : ''
                            }`}
                        >
                            {formatProfit(summary.profit)}
                        </span>
                    </div>
                    <div className={styles.historySummaryItem}>
                        <span className={styles.historySummaryLabel}>Registrations</span>
                        <span className={styles.historySummaryValue}>
                            {summary.totalRegistrations.toLocaleString('en-US')}
                        </span>
                    </div>
                </div>
            )}

            <div className={styles.historyList} ref={listRef}>
                {items.map((entry) => (
                    <div className={styles.historyRow} key={entry.id}>
                        <span className={`${styles.historyBadge} ${actionClass(entry.action)}`}>
                            {ACTION_LABELS[entry.action] ?? entry.action}
                        </span>
                        <span className={styles.historyPlayer} title={playerOf(entry)}>
                            {playerOf(entry)}
                        </span>
                        <span className={styles.historyAmount}>{formatAmount(entry.amount)}</span>
                        <span className={styles.historyWhen}>{formatWhen(entry.createdAt)}</span>
                    </div>
                ))}

                {!loading && !error && items.length === 0 && (
                    <p className={styles.detailEmpty}>No actions recorded for this terminal yet.</p>
                )}

                {error && <p className={styles.historyError}>{error}</p>}

                {/* Watched by the observer above — reaching it loads the next page. */}
                <div ref={sentinelRef} className={styles.historySentinel}>
                    {loading && <span className={styles.historyMuted}>Loading…</span>}
                    {!loading && !hasMore && items.length > 0 && (
                        <span className={styles.historyMuted}>End of history</span>
                    )}
                </div>
            </div>

            {total > 0 && (
                <div className={styles.historyFooter}>
                    Showing {items.length} of {total.toLocaleString('en-US')}
                </div>
            )}
        </>
    );
};

export default AtmTerminalHistory;
